import type { FileMap } from "./datapack";
import { GOALS, goalText, rankTier, sanitizeGrant, type Grant, type Rank } from "./ranks";
import { toSnbt } from "./snbt";

const NS = "ranks";
const FN = `data/${NS}/function`;
const DATA_MIN_FORMAT = 94;
const MAX_FORMAT = 999;
const EFFECT_SECONDS = 15;
const MENU = `${NS}_menu`;
const KIT = `${NS}_kit`;
const KIT_DAY = `${NS}_kitday`;
const CONST = `${NS}_const`;
const FREE = `${NS}_free`;

export function buildRankPack(input: Rank[], grants: Grant[] = []): FileMap {
  const ranks = [...uniqueById(input)].sort((a, b) => rankTier(a.goal) - rankTier(b.goal));
  const ids = new Set(ranks.map((r) => r.id));
  const valid = grants.map(sanitizeGrant).filter((g): g is Grant => g !== null && ids.has(g.rankId));
  const stats = [...new Set(ranks.map((r) => r.goal.stat))];
  return {
    "pack.mcmeta": json({ pack: { description: "Family ranks - made with mods.maniginam.dev", min_format: DATA_MIN_FORMAT, max_format: MAX_FORMAT } }),
    "data/minecraft/tags/function/load.json": json({ values: [`${NS}:load`] }),
    "data/minecraft/tags/function/tick.json": json({ values: [`${NS}:tick`] }),
    [`data/${NS}/advancement/welcome.json`]: json({ criteria: { join: { trigger: "minecraft:tick" } }, rewards: { function: `${NS}:welcome` } }),
    [`${FN}/load.mcfunction`]: lines(loadLines(ranks, stats)),
    [`${FN}/second.mcfunction`]: lines(secondLines(ranks, stats, valid)),
    [`${FN}/tick.mcfunction`]: lines(tickLines()),
    [`${FN}/kit.mcfunction`]: lines(kitLines(ranks)),
    [`${FN}/menu.mcfunction`]: lines(menuLines(ranks)),
    [`${FN}/welcome.mcfunction`]: lines([
      `tellraw @s [{text:"Ranks are on! ",color:"gold",bold:true},{text:"Type /trigger ${MENU} or click here to see how to earn them",` +
      `color:"green",underlined:true,click_event:{action:"run_command",command:"/trigger ${MENU}"}}]`,
    ]),
  };
}

function loadLines(ranks: Rank[], stats: string[]): string[] {
  return [
    ...stats.flatMap((stat) => [
      ...GOALS[stat].criteria.map((criteria, i) => `scoreboard objectives add ${raw(stat, i)} ${criteria}`),
      `scoreboard objectives add ${total(stat)} dummy`,
      `scoreboard objectives add ${shown(stat)} dummy`,
    ]),
    `scoreboard objectives add ${MENU} trigger`,
    `scoreboard objectives add ${KIT} trigger`,
    `scoreboard objectives add ${KIT_DAY} dummy`,
    `scoreboard objectives add ${CONST} dummy`,
    ...stats.map((stat) => `scoreboard players set #${GOALS[stat].scale} ${CONST} ${GOALS[stat].scale}`),
    ...ranks.flatMap((r) => [
      `team add ${team(r)}`,
      `team modify ${team(r)} prefix ${toSnbt({ text: `[${r.name}] `, color: r.color, bold: r.bold })}`,
      `team modify ${team(r)} color ${r.color}`,
    ]),
    `advancement revoke @a only ${NS}:welcome`,
    `schedule function ${NS}:second 20t replace`,
  ];
}

function secondLines(ranks: Rank[], stats: string[], grants: Grant[]): string[] {
  return [
    ...stats.flatMap((stat) => [
      `execute as @a run scoreboard players operation @s ${total(stat)} = @s ${raw(stat, 0)}`,
      ...GOALS[stat].criteria.slice(1).map((_, i) =>
        `execute as @a run scoreboard players operation @s ${total(stat)} += @s ${raw(stat, i + 1)}`),
      `execute as @a run scoreboard players operation @s ${shown(stat)} = @s ${total(stat)}`,
      `execute as @a run scoreboard players operation @s ${shown(stat)} /= #${GOALS[stat].scale} ${CONST}`,
    ]),
    ...ranks.map((r) => {
      const threshold = r.goal.amount * GOALS[r.goal.stat].scale;
      return threshold > 0 ? `tag @a[scores={${total(r.goal.stat)}=${threshold}..}] add ${has(r)}` : `tag @a add ${has(r)}`;
    }),
    ...grants.map((g) => `tag @a[name=${g.player}] add ${NS}_has_${g.rankId}`),
    `tag @a remove ${FREE}`,
    `tag @a[team=] add ${FREE}`,
    ...ranks.map((r) => `tag @a[team=${team(r)}] add ${FREE}`),
    ...ranks.map((r) => `team join ${team(r)} @a[tag=${has(r)},tag=${FREE}]`),
    ...ranks.flatMap((r) => [
      ...r.effects.map((e) => `effect give @a[tag=${has(r)}] minecraft:${e} ${EFFECT_SECONDS} 0 true`),
      ...(r.glow ? [`effect give @a[tag=${has(r)}] minecraft:glowing ${EFFECT_SECONDS} 0 true`] : []),
      ...(r.particle ? [`execute as @a[team=${team(r)}] at @s run particle minecraft:${r.particle} ~ ~0.2 ~ 0.3 0.1 0.3 0 4`] : []),
    ]),
    `schedule function ${NS}:second 20t replace`,
  ];
}

function tickLines(): string[] {
  return [
    `execute as @a[scores={${MENU}=1..}] run function ${NS}:menu`,
    `execute as @a[scores={${KIT}=1..}] run function ${NS}:kit`,
    `scoreboard players set @a ${MENU} 0`,
    `scoreboard players set @a ${KIT} 0`,
    `scoreboard players enable @a ${MENU}`,
    `scoreboard players enable @a ${KIT}`,
  ];
}

function kitLines(ranks: Rank[]): string[] {
  return [
    `execute store result score #today ${KIT_DAY} run time query day`,
    `execute if score @s ${KIT_DAY} >= #today ${KIT_DAY} run return run tellraw @s {text:"You already got your kits today. Come back tomorrow!",color:"yellow"}`,
    `scoreboard players operation @s ${KIT_DAY} = #today ${KIT_DAY}`,
    ...ranks.flatMap((r) => r.kit.map((k) => `give @s[tag=${has(r)}] minecraft:${k.item} ${k.count}`)),
    `tellraw @s {text:"Daily kits claimed!",color:"green"}`,
  ];
}

function menuLines(ranks: Rank[]): string[] {
  return [
    `tellraw @s {text:"=== Ranks (earn them by playing!) ===",color:"gold",bold:true}`,
    ...ranks.flatMap((r) => {
      const tag = { text: `[${r.name}]`, color: r.color, bold: r.bold };
      const goal = goalText(r.goal);
      return [
        `execute if entity @s[tag=${has(r)}] run tellraw @s ${toSnbt([{ text: "✔ ", color: "green" }, tag, { text: ` ${r.motto}`, color: "gray" }])}`,
        `execute unless entity @s[tag=${has(r)}] run tellraw @s ${toSnbt([
          { text: "✘ ", color: "dark_gray" }, tag, { text: ` ${goal} (you: `, color: "gray" },
          { score: { name: "@s", objective: shown(r.goal.stat) }, color: "white" }, { text: ")", color: "gray" },
        ])}`,
      ];
    }),
    `tellraw @s {text:"Type /trigger ${KIT} once a day for your rank kits!",color:"aqua"}`,
  ];
}

function uniqueById(ranks: Rank[]): Rank[] {
  const seen = new Set<string>();
  return ranks.filter((r) => !seen.has(r.id) && seen.add(r.id));
}

const team = (r: Rank) => `${NS}_${r.id}`;
const has = (r: Rank) => `${NS}_has_${r.id}`;
const raw = (stat: string, i: number) => `rk_${stat}_${i}`;
const total = (stat: string) => `${NS}_${stat}`;
const shown = (stat: string) => `${NS}_show_${stat}`;

function json(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

function lines(commands: string[]): string {
  return commands.join("\n") + "\n";
}
